import { SOMNIO_PROTOCOL_CONSTANTS, utf8ByteLength } from './constants.ts';
import { OversizedFrameError, UnrecognizedTagError, WireDecodingError } from './errors.ts';
import type { SomnioMessage } from './message.ts';
import {
  decodeAdminSayMessage,
  decodeAskTaskMessage,
  decodeBlowMessage,
  decodeBuyMessage,
  decodeClientSayMessage,
  decodeCompleteTaskMessage,
  decodeConditionMessage,
  decodeCorrectionMessage,
  decodeDoorRefusedMessage,
  decodeEmptyMessage,
  decodeEnergy,
  decodeEnterSpaceMessage,
  decodeEntityMessage,
  decodeEquipToggleMessage,
  decodeHelloMessage,
  decodeInventoryMessage,
  decodeLeaveMessage,
  decodeLoginMessage,
  decodeLoginResultMessage,
  decodeLucidityMessage,
  decodeMendMessage,
  decodeMoveMessage,
  decodeMovesMessage,
  decodeOtherSessionsMessage,
  decodeOtherSessionsRevokedMessage,
  decodeRaisingMessage,
  decodeRedeemSessionMessage,
  decodeRegisterMessage,
  decodeRegisterResultMessage,
  decodeRevokeSessionMessage,
  decodeSayMessage,
  decodeSectorMessage,
  decodeSellMessage,
  decodeSessionRevokedMessage,
  decodeSessionTokenMessage,
  decodeStudyMessage,
  decodeSwingMessage,
  decodeTalkMessage,
  decodeTendMessage,
  decodeUseDoorMessage,
  decodeUseItemMessage,
} from './payloads.ts';
import { isSomnioMessageTag, type SomnioMessageTag } from './tags.ts';
import { requireObject } from './validate.ts';

/**
 * The gameplay frame codec. Frames are JSON over WebSocket **text** frames; the boundary
 * converts to and from `string` at the frame edge, never `ArrayBuffer` — the server closes on
 * any binary frame.
 */

type PayloadDecoder = (container: Record<string, unknown>, path: string) => unknown;

/**
 * Tag-to-decoder table. Exhaustive over `SomnioMessageTag` by the `Record` type, so adding a
 * tag to the union without a decoder is a compile error rather than a runtime surprise.
 */
const PAYLOAD_DECODERS: Record<SomnioMessageTag, PayloadDecoder> = {
  login: decodeLoginMessage,
  register: decodeRegisterMessage,
  move: decodeMoveMessage,
  clientSay: decodeClientSayMessage,
  equipToggle: decodeEquipToggleMessage,
  talk: decodeTalkMessage,
  swing: decodeSwingMessage,
  tend: decodeTendMessage,
  useDoor: decodeUseDoorMessage,
  askTask: decodeAskTaskMessage,
  completeTask: decodeCompleteTaskMessage,
  abandonTask: decodeEmptyMessage,
  study: decodeStudyMessage,
  buy: decodeBuyMessage,
  sell: decodeSellMessage,
  wake: decodeEmptyMessage,
  useItem: decodeUseItemMessage,
  redeemSession: decodeRedeemSessionMessage,
  revokeSession: decodeRevokeSessionMessage,
  revokeOtherSessions: decodeEmptyMessage,
  hello: decodeHelloMessage,
  loginResult: decodeLoginResultMessage,
  registerResult: decodeRegisterResultMessage,
  enterSpace: decodeEnterSpaceMessage,
  sector: decodeSectorMessage,
  entity: decodeEntityMessage,
  moves: decodeMovesMessage,
  correction: decodeCorrectionMessage,
  doorRefused: decodeDoorRefusedMessage,
  serverSay: decodeSayMessage,
  energy: decodeEnergy,
  inventory: decodeInventoryMessage,
  leave: decodeLeaveMessage,
  lucidity: decodeLucidityMessage,
  condition: decodeConditionMessage,
  blow: decodeBlowMessage,
  raising: decodeRaisingMessage,
  mend: decodeMendMessage,
  adminSay: decodeAdminSayMessage,
  sessionToken: decodeSessionTokenMessage,
  sessionRevoked: decodeSessionRevokedMessage,
  otherSessions: decodeOtherSessionsMessage,
  otherSessionsRevoked: decodeOtherSessionsRevokedMessage,
};

/**
 * Encodes a message to the JSON text a WebSocket `send` takes. Guards `maxFrameLength` so an
 * abusive frame throws here rather than tripping the receiver's hard close.
 */
export function encodeSomnioMessage(message: SomnioMessage): string {
  const text = JSON.stringify({ tag: message.tag, payload: message.payload });
  const byteCount = utf8ByteLength(text);
  if (byteCount > SOMNIO_PROTOCOL_CONSTANTS.maxFrameLength) {
    throw new OversizedFrameError(byteCount);
  }
  return text;
}

/**
 * Decodes one inbound text frame. Rejected: malformed JSON, a non-object frame, a non-string
 * discriminator, an unknown tag, a known tag whose payload fails validation, and a frame over
 * `maxFrameLength`. The size cap lives here
 * rather than only at the WebSocket layer because the browser `WebSocket` exposes no
 * `maxFrameSize` knob, so the decoder is the one place both peers can enforce it.
 */
export function decodeSomnioMessage(frame: string): SomnioMessage {
  const byteCount = utf8ByteLength(frame);
  if (byteCount > SOMNIO_PROTOCOL_CONSTANTS.maxFrameLength) {
    throw new OversizedFrameError(byteCount);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(frame);
  } catch (cause) {
    throw new WireDecodingError('<frame>', `malformed JSON (${(cause as Error).message})`);
  }

  const container = requireObject(parsed, '<frame>');
  const rawTag = container['tag'];
  if (typeof rawTag !== 'string') {
    throw new WireDecodingError('<frame>.tag', 'expected a string discriminator');
  }
  if (!isSomnioMessageTag(rawTag)) {
    throw new UnrecognizedTagError(rawTag);
  }

  const payloadContainer = requireObject(container['payload'], `<frame>.payload(${rawTag})`);
  const payload = PAYLOAD_DECODERS[rawTag](payloadContainer, rawTag);
  return { tag: rawTag, payload } as SomnioMessage;
}
